import { useEffect, useState } from 'react';
import { useAuth } from './useAuth';
import { useEmpresasContext } from '../context/EmpresasContext';
import { AsignacionesUsuarioOperadorService } from '../services/asignacionesUsuarioOperadorService';

// ===============================
// TIPOS
// ===============================

export interface FiltroOperadoresResult {
  operadoresPermitidos: number[] | null; // null = todos, array = IDs específicos
  isLoading: boolean;
  error: string | null;
  tieneAccesoCompleto: boolean; // true si es supervisor/admin
}

// ===============================
// HOOK PRINCIPAL
// ===============================

export function useFiltroOperadoresUsuario() {
  const [operadoresPermitidos, setOperadoresPermitidos] = useState<number[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { usuario } = useAuth();
  const { empresaActiva } = useEmpresasContext();

  const tieneAccesoCompleto = usuario?.tipo_usuario === 'supervisor' || usuario?.tipo_usuario === 'administrador';

  useEffect(() => {
    const cargarFiltroOperadores = async () => {
      try {
        setIsLoading(true);
        setError(null);

        if (!usuario || !empresaActiva?.empresa?.id) {
          setOperadoresPermitidos(null);
          return;
        }

        // Si es supervisor o administrador: acceso completo
        if (tieneAccesoCompleto) {
          setOperadoresPermitidos(null); // null = todos los operadores
          return;
        }

        // Si es operador: solo operadores asignados
        const operadoresAsignados = await AsignacionesUsuarioOperadorService.obtenerMisOperadoresAsignados();
        const idsPermitidos = operadoresAsignados.map(o => o.id);

        setOperadoresPermitidos(idsPermitidos);
      } catch (err: any) {
        console.error('Error cargando filtro de operadores:', err);
        setError(err.message || 'Error cargando permisos de operadores');

        // En caso de error, permitir acceso completo para no bloquear
        setOperadoresPermitidos(null);

      } finally {
        setIsLoading(false);
      }
    };

    cargarFiltroOperadores();
  }, [usuario?.id, empresaActiva?.empresa?.id, tieneAccesoCompleto]);

  // Función para verificar si un operador específico está permitido
  const tieneOperadorPermitido = (operadorId: number): boolean => {
    // Si tiene acceso completo o operadoresPermitidos es null = todos permitidos
    if (tieneAccesoCompleto || operadoresPermitidos === null) {
      return true;
    }

    // Si es operador, verificar si el operador está en su lista
    return operadoresPermitidos.includes(operadorId);
  };

  // Función para filtrar una lista de operadores
  const filtrarOperadores = <T extends { id: number }>(operadores: T[]): T[] => {
    // Si tiene acceso completo, retornar todos
    if (tieneAccesoCompleto || operadoresPermitidos === null) {
      return operadores;
    }

    // Si es operador, filtrar por operadores asignados
    return operadores.filter(operador => operadoresPermitidos.includes(operador.id));
  };

  return {
    operadoresPermitidos,
    isLoading,
    error,
    tieneAccesoCompleto,
    tieneOperadorPermitido,
    filtrarOperadores
  };
}
